import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ListChecks, Search, Filter, Calendar, Pause, Check, Clock,
  FileText, Package, Wrench, Receipt, Bike, Hash } from "lucide-react";
import { toast } from "sonner";
import { useApp, usePerm } from "@/context/AppContext";
import { avList } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { AV_STATUSES, AV_STATUS_TONES, statusLabel, CLAIM_TYPES } from "./avShared";

// Workbench tabs — Documents intentionally excluded (lives on the Detail
// page). Estimate aggregates everything from intake up to the moment parts
// ordering begins, so the workshop sees a single bucket of "needs estimate
// work".
const TABS = [
  { key: "ALL", label: "All", statuses: null },
  { key: "estimate", label: "Estimate", statuses: [
    "vehicle_received", "estimate_pending", "customer_approval_pending", "documents_pending",
  ] },
  { key: "parts", label: "Parts", statuses: ["parts_pending"] },
  { key: "repair", label: "Repair", statuses: ["repair_in_progress"] },
  { key: "billing", label: "Billing", statuses: ["billing_pending"] },
  { key: "ready", label: "Ready", statuses: ["ready_delivery"] },
  { key: "on_hold", label: "Hold", statuses: ["on_hold"] },
  { key: "delivered", label: "Delivered", statuses: ["delivered"] },
];

// Progress stepper stages — every AV traverses these 5 milestones in order.
// We map the AV's `status` to an index so we can mark each step as
// done / current / pending consistently across the workbench.
const STAGES = [
  { key: "estimate", label: "Estimate", icon: FileText },
  { key: "parts",    label: "Parts",    icon: Package },
  { key: "repair",   label: "Repair",   icon: Wrench },
  { key: "billing",  label: "Billing",  icon: Receipt },
  { key: "delivery", label: "Delivery", icon: Bike },
];

const STATUS_STAGE_INDEX = {
  draft: 0,
  vehicle_received: 0,
  estimate_pending: 0,
  customer_approval_pending: 0,
  documents_pending: 0,
  parts_pending: 1,
  repair_in_progress: 2,
  billing_pending: 3,
  ready_delivery: 4,
  delivered: 5,           // past the last stage → all checked
  on_hold: -1,            // handled separately (paused)
  cancelled: -2,          // handled separately (dimmed)
};

const AvWorkbench = () => {
  const { currentUser, locations, currentLocation } = useApp();
  const canAdd = usePerm("av", "add");
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const allowed = useMemo(
    () => locations.filter(
      (l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)
    ),
    [locations, currentUser]
  );

  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "ALL");
  const [claimType, setClaimType] = useState(params.get("claim_type") || "ALL");
  const [insuranceCompany, setInsuranceCompany] = useState("");
  const [model, setModel] = useState("");
  const [ageing, setAgeing] = useState(params.get("ageing") || "ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Initial tab from URL param (e.g. dashboard cards pass ?status=parts_pending).
  const [tab, setTab] = useState(() => {
    const s = params.get("status");
    if (!s) return "ALL";
    const match = TABS.find((t) => (t.statuses || []).includes(s));
    return match?.key || "ALL";
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = {};
      if (q) p.q = q;
      if (currentLocation && currentLocation !== "ALL") p.location_code = currentLocation;
      if (statusFilter !== "ALL") p.status = statusFilter;
      if (claimType !== "ALL") p.claim_type = claimType;
      if (insuranceCompany) p.insurance_company = insuranceCompany;
      if (model) p.model = model;
      if (ageing !== "ALL") p.ageing = ageing;
      if (fromDate) p.from_date = fromDate;
      if (toDate) p.to_date = toDate;
      const res = await avList(p);
      setRows(res?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AV list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, currentLocation, statusFilter, claimType, insuranceCompany, model, ageing, fromDate, toDate]);

  // Per-tab counts based on the loaded `rows` (after server-side filters).
  // We compute once and reuse for both the chip counter and the visible list.
  const counts = useMemo(() => {
    const out = { ALL: rows.length };
    for (const t of TABS) {
      if (!t.statuses) continue;
      out[t.key] = rows.filter((r) => t.statuses.includes(r.status)).length;
    }
    return out;
  }, [rows]);

  const visibleRows = useMemo(() => {
    const t = TABS.find((x) => x.key === tab);
    if (!t?.statuses) return rows;
    return rows.filter((r) => t.statuses.includes(r.status));
  }, [rows, tab]);

  return (
    <div className="space-y-3" data-testid="av-workbench-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <ListChecks className="w-5 h-5 text-yamaha-700" />
        </div>
        <div>
          <h1 className="text-[20px] font-semibold text-yamaha-800 leading-none tracking-tight">AV Workbench</h1>
          <p className="text-[11.5px] text-[#747684] mt-1">
            Operational queue. Ageing and likely delivery emphasised — scan fast, click in.
          </p>
        </div>
        <div className="ml-auto">
          {canAdd && (
          <button
            onClick={() => navigate("/av/new")}
            data-testid="av-wb-new-btn"
            className="px-3.5 py-1.5 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold"
          >
            + New AV
          </button>
          )}
        </div>
      </header>

      {/* Filters (compact) */}
      <div className="bg-white border border-[#e2e1eb] rounded-lg p-2.5 flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1.5 text-[#747684] pb-1.5">
          <Filter className="w-3.5 h-3.5" />
          <span className="text-[11.5px] font-semibold text-[#444653]">Filters:</span>
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md pb-1.5">
          <Search className="w-3.5 h-3.5 text-[#747684] absolute left-2.5 top-2" />
          <input
            data-testid="av-wb-search"
            placeholder="AV ID, Customer, Mobile, Reg No, VIN, JC..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 border border-[#e2e1eb] rounded-md text-[12px]"
          />
        </div>
        <Filt label="Claim">
          <select value={claimType} onChange={(e) => setClaimType(e.target.value)} data-testid="av-wb-claim">
            <option value="ALL">All</option>
            {CLAIM_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Filt>
        <Filt label="Ageing">
          <select value={ageing} onChange={(e) => setAgeing(e.target.value)} data-testid="av-wb-ageing">
            <option value="ALL">All</option>
            <option value="red">High</option>
            <option value="yellow">Medium</option>
            <option value="green">Low</option>
          </select>
        </Filt>
        <Filt label="Insurance">
          <input value={insuranceCompany} onChange={(e) => setInsuranceCompany(e.target.value)} data-testid="av-wb-insurance" placeholder="contains…" />
        </Filt>
        <Filt label="Model">
          <input value={model} onChange={(e) => setModel(e.target.value)} data-testid="av-wb-model" placeholder="contains…" />
        </Filt>
        <Filt label="From">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="av-wb-from" />
        </Filt>
        <Filt label="To">
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="av-wb-to" />
        </Filt>
        <div className="ml-auto pb-1.5 text-[10.5px] text-[#747684]">
          {loading ? "Loading…" : `${visibleRows.length} of ${rows.length} shown`}
        </div>
      </div>

      {/* Compact counter tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="av-wb-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          const count = counts[t.key] || 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`av-wb-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold transition-colors ${
                active
                  ? "border-yamaha-500 bg-yamaha-50 text-yamaha-800"
                  : "border-[#e2e1eb] bg-white text-[#444653] hover:bg-[#faf8ff]"
              }`}
            >
              <span>{t.label}</span>
              <span className={`text-[10.5px] font-bold tabular-nums px-1.5 py-px rounded-full ${
                active ? "bg-yamaha-700 text-white" : "bg-[#f1f0f7] text-[#444653]"
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Card list */}
      <div className="space-y-2" data-testid="av-wb-rows">
        {visibleRows.length === 0 && (
          <div className="bg-white border border-[#e2e1eb] rounded-lg px-4 py-10 text-center text-[#747684] text-[12.5px]">
            No accidental vehicles match the current filters.
          </div>
        )}
        {visibleRows.map((r) => (
          <AvRow key={r.id} av={r} onOpen={() => navigate(`/av/${r.id}`)} />
        ))}
      </div>
    </div>
  );
};

const Filt = ({ label, children }) => (
  <div className="flex flex-col pb-1.5">
    <label className="text-[9.5px] font-bold uppercase tracking-wider text-[#747684] mb-0.5">{label}</label>
    {React.cloneElement(children, {
      className: "px-2 py-1 border border-[#e2e1eb] rounded-md text-[12px] min-w-[130px]",
    })}
  </div>
);

// Derive the estimate sub-status for the card pill. Uses the latest entry in
// `av.estimates` if present — otherwise falls back to "Estimate Pending".
const estimateSubStatus = (av) => {
  const latest = (av.estimates || []).slice(-1)[0];
  if (!latest) return { label: "Estimate Pending", tone: "amber" };
  const s = latest.customer_approval_status || "pending";
  if (s === "approved") return { label: "Approved", tone: "emerald" };
  if (s === "rejected") return { label: "Rejected", tone: "rose" };
  return { label: "Waiting Customer Approval", tone: "amber" };
};

const SUB_TONES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
};

const initialsOf = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts[0]) return "?";
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (a + b).toUpperCase();
};

const AvRow = ({ av, onOpen }) => {
  const bucket = av.ageing_bucket || "green";
  const wrapTones = {
    red: "bg-rose-50/30 border-rose-200",
    yellow: "bg-amber-50/30 border-amber-200",
    green: "bg-white border-[#e2e1eb]",
  };
  const statusTone = AV_STATUS_TONES[av.status] || AV_STATUS_TONES.draft;
  const inEstimate = ["vehicle_received", "estimate_pending", "customer_approval_pending", "documents_pending"].includes(av.status);
  const estSub = inEstimate ? estimateSubStatus(av) : null;
  const ageingTone = bucket === "red" ? "text-rose-700" : bucket === "yellow" ? "text-amber-700" : "text-emerald-700";

  return (
    <button
      onClick={onOpen}
      data-testid={`av-row-${av.av_number}`}
      className={`w-full text-left border rounded-xl px-4 py-2.5 hover:shadow-md hover:border-yamaha-300 transition-all ${wrapTones[bucket]}`}
    >
      <div className="grid grid-cols-12 gap-4 items-center min-h-[66px]">
        {/* 1. IDENTITY (col-span 4) */}
        <div className="col-span-12 md:col-span-4 flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-full bg-yamaha-50 border border-yamaha-100 flex items-center justify-center flex-shrink-0">
            <Bike className="w-5 h-5 text-yamaha-700" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono font-bold text-[13.5px] text-yamaha-800 leading-tight truncate">
              {av.av_number}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[12px] min-w-0">
              <span className="font-semibold text-[#1a1b22] truncate">{av.customer_name}</span>
              <span className="text-[#cfcdd8] flex-shrink-0">•</span>
              <span className="text-[#444653] truncate">{av.model_name}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-[#747684] flex-wrap">
              <span className="inline-flex items-center gap-0.5">
                <Hash className="w-2.5 h-2.5" />
                <span className="font-mono font-semibold text-[#444653]">{av.registration_number}</span>
              </span>
              <span className="text-[#cfcdd8]">·</span>
              <span className="inline-flex items-center gap-0.5">
                <Wrench className="w-2.5 h-2.5" />
                <span className="font-mono font-semibold text-[#444653]">JC {av.job_card_no}</span>
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[10.5px] flex-wrap"
                 data-testid={`av-row-recv-${av.av_number}`}>
              <span className="inline-flex items-center gap-0.5 text-[#747684]">
                <Calendar className="w-2.5 h-2.5" />
                Received
                <span className="font-semibold text-[#1a1b22] ml-0.5">
                  {av.receiving_date ? fmtDate(av.receiving_date) : "—"}
                </span>
              </span>
              <span className="text-[#cfcdd8]">·</span>
              <span className={`inline-flex items-center gap-0.5 font-semibold ${ageingTone}`}>
                <Clock className="w-2.5 h-2.5" />
                {av.total_ageing_days || 0}d ageing
              </span>
            </div>
          </div>
        </div>

        {/* 2. PROCESS STAGE TRACKER (col-span 4) */}
        <div className="col-span-12 md:col-span-4 flex justify-center">
          <ProgressStepper av={av} />
        </div>

        {/* 3. CURRENT STATUS (col-span 2) — max 2 pills */}
        <div className="col-span-6 md:col-span-2 flex flex-col items-start md:items-center gap-1">
          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-0.5 text-center ${statusTone}`}>
            {statusLabel(av.status)}
          </span>
          {estSub && (
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-0.5 text-center ${SUB_TONES[estSub.tone]}`}>
              {estSub.label}
            </span>
          )}
          {!estSub && av.on_hold && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-300 rounded-full px-2.5 py-0.5">
              <Pause className="w-2.5 h-2.5" /> Hold {av.hold_days || 0}d
            </span>
          )}
        </div>

        {/* 4. LAST UPDATED (col-span 2) */}
        <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-2">
          <div className="w-9 h-9 rounded-full bg-[#f4f3fc] border border-[#e2e1eb] flex items-center justify-center flex-shrink-0">
            <span className="text-[10.5px] font-bold text-yamaha-700 tracking-wide">
              {initialsOf(av.updated_by)}
            </span>
          </div>
          <div className="text-right leading-tight min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#747684] font-semibold">
              Updated by
            </div>
            <div className="text-[11.5px] font-semibold text-[#1a1b22] truncate max-w-[130px]">
              {av.updated_by || "—"}
            </div>
            <div className="text-[10px] text-[#747684]">
              {fmtDateTime(av.updated_at)}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

const ProgressStepper = ({ av }) => {
  const idx = STATUS_STAGE_INDEX[av.status];
  const onHold = idx === -1;
  const cancelled = idx === -2;
  const delivered = idx >= STAGES.length;

  const circleClass = (i) => {
    if (cancelled) return "bg-white border-[#e2e1eb] text-[#a0a2b0]";
    if (onHold) return "bg-slate-50 border-slate-300 text-slate-500";
    if (delivered || i < idx) return "bg-emerald-50 border-emerald-500 text-emerald-700"; // done
    if (i === idx) return "bg-yamaha-50 border-yamaha-500 text-yamaha-700 ring-2 ring-yamaha-100"; // current
    return "bg-white border-[#e2e1eb] text-[#a0a2b0]"; // pending
  };
  const labelClass = (i) => {
    if (cancelled || onHold) return "text-[#a0a2b0]";
    if (delivered || i < idx) return "text-emerald-700 font-semibold";
    if (i === idx) return "text-yamaha-700 font-bold";
    return "text-[#a0a2b0]";
  };
  const lineClass = (i) => {
    if (cancelled || onHold) return "bg-[#e2e1eb]";
    if (delivered || i < idx) return "bg-emerald-400";
    return "bg-[#e2e1eb]";
  };

  return (
    <div className="w-full flex items-center" data-testid={`av-stepper-${av.av_number}`}>
      {STAGES.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${circleClass(i)}`}>
              {(delivered || i < idx) && !onHold && !cancelled
                ? <Check className="w-4 h-4" strokeWidth={2.5} />
                : <s.icon className="w-4 h-4" strokeWidth={2} />}
            </div>
            <span className={`text-[9.5px] uppercase tracking-wider ${labelClass(i)}`}>{s.label}</span>
          </div>
          {i < STAGES.length - 1 && (
            <div className="flex-1 h-0.5 min-w-[10px] mx-1 rounded-full transition-colors relative -translate-y-1.5"
                 style={{ backgroundColor: undefined }}>
              <div className={`w-full h-full rounded-full ${lineClass(i)}`}></div>
            </div>
          )}
        </React.Fragment>
      ))}
      {onHold && (
        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <Pause className="w-2.5 h-2.5" /> Paused
        </span>
      )}
    </div>
  );
};

export default AvWorkbench;
